import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Box, Grid, TextField, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/InsertLink";
import Button from "@/components/button";
import Table from "./table";
import CustomizedSnackbars from "@/components/snackBar";

import { useQuery } from "@apollo/client";
import { STORES_WITH_PRICES } from "@/services/storeService";
import { RECENT_PRICE_CHANGE } from "@/services/productService";

import useStyles from "./styles";
import { Colors } from "@/theme";
import { MERCHANT_ID, LIVE_PRICE_URL } from "@/constants/env";
import { PRICE_HISTORY_LIMIT } from "@/constants/config";
import { contains } from "@/utils/stringUtil";

const ManagePrice = () => {
  const { t } = useTranslation();

  const classes = useStyles();
  const [searchKey, setSearchKey] = useState("");
  const [snackData, setSnackData] = useState(null);

  const {
    data: storesData,
    loading: isLoadingStores,
    refetch: refetchStores,
  } = useQuery(STORES_WITH_PRICES, {
    variables: {
      limit: PRICE_HISTORY_LIMIT,
    },
    context: {
      headers: {
        merchantId: MERCHANT_ID,
      },
    },
    fetchPolicy: "no-cache",
  });
  const stores = storesData?.stores || [];

  const {
    data,
    loading: isLoadingPriceChange,
    refetch: refetchPriceChange,
  } = useQuery(RECENT_PRICE_CHANGE, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
      },
    },
    fetchPolicy: "no-cache",
  });
  const recentPriceChange = data?.recent_price_change;

  const filteredStores = stores.filter((store) =>
    contains(store.name, searchKey)
  );

  const onPriceUpdateSuccess = () => {
    refetchPriceChange();
    refetchStores();
    setSnackData({
      message: t("manage_price_screen.price_updated"),
      type: "success",
    });
  };

  const onCopyLivePriceUrl = () => {
    navigator.clipboard.writeText(LIVE_PRICE_URL).then(() => {
      setSnackData({
        type: "success",
        message: t("manage_price_screen.url_copied"),
      });
    });
  };

  return (
    <div>
      <Box>
        <div className={classes.managePriceText}>
          {t("manage_price_screen.manage_price")}
        </div>
      </Box>
      <Box marginTop={4}>
        {isLoadingPriceChange ? (
          <Skeleton variant="rectangular" width={250} height={49} />
        ) : (
          <>
            <div className={classes.updateTimeText}>
              {!!recentPriceChange &&
                moment
                  .utc(recentPriceChange)
                  .local()
                  .format("DD MMM YYYY HH:mm:ss")}
            </div>
            <div className={classes.lastUpdatedText}>
              {t("manage_price_screen.last_updated")}
            </div>
          </>
        )}
      </Box>

      <Grid container spacing={6} mt={3}>
        <Grid item xs={12} md={8.5}>
          <TextField
            placeholder={t("manage_price_screen.enter_site_name")}
            fullWidth
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <SearchIcon sx={{ color: Colors.GRAY_BORDER, fontSize: 30 }} />
              ),
            }}
          />
        </Grid>
        <Grid item xs={3} md={1}>
          <Button
            variant="outlined"
            fullWidth
            className={classes.linkButton}
            onClick={onCopyLivePriceUrl}
          >
            <LinkIcon sx={{ color: Colors.GRAY_BORDER, fontSize: 70 }} />
          </Button>
        </Grid>
        <Grid item xs={9} md={2.5}>
          <Button
            disabled
            variant="outlined"
            fullWidth
            className={classes.downloadButton}
          >
            {t("manage_price_screen.download")}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Table
          loading={isLoadingStores}
          stores={filteredStores}
          onPriceUpdateSuccess={onPriceUpdateSuccess}
        />
      </Box>

      <CustomizedSnackbars
        open={!!snackData}
        onClose={() => setSnackData(null)}
        type={snackData?.type}
        message={snackData?.message}
      />
    </div>
  );
};

export default ManagePrice;
